import { Flex, Link } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as ReactRouterLink } from "react-router";

import { RanaButton, RanaTable } from "@nens/ranalib";
import { capitalizeFirst, listToString } from "@nens/shared-utils";

import { BackButton } from "@/components/back-button";

import { AdminTenantCreateOrEdit } from "../admin-tenant/admin-tenant-create-or-edit";
import { AdminDeleteTenantModal } from "../admin-tenant/components/admin-delete-tenant-modal";

import { useAdminTenantList } from "./hooks/use-admin-tenant-list";

export function AdminTenantList() {
  const intl = useIntl();
  const {
    page,
    setPageSize,
    setPage,
    orderings,
    setOrdering,
    tenantList,
    isSkeleton,
    numPages,
    limit,
    getTableActions,
    getActions,
    modal,
    onClose,
    canDeleteTenant,
    isCreating,
    setIsCreating,
  } = useAdminTenantList();

  if (isCreating) {
    return (
      <>
        <BackButton back={() => setIsCreating(false)}>
          <FormattedMessage id="button.back" defaultMessage="Back" />
        </BackButton>
        <AdminTenantCreateOrEdit type="creating" close={() => setIsCreating(false)} />
      </>
    );
  }

  return (
    <>
      <Flex w="full" justifyContent="flex-end">
        <RanaButton variant="secondary" onClick={() => setIsCreating(true)}>
          <FormattedMessage id="button.create_organisation" defaultMessage="Create organisation" />
        </RanaButton>
      </Flex>
      {modal?.type === "delete" && (
        <AdminDeleteTenantModal tenants={modal.tenants} onClose={onClose} />
      )}
      <Flex direction="column" gap={2} mb={11} pt={2}>
        <RanaTable
          emptyElement={<RanaTable.Empty>There are no organisations :(</RanaTable.Empty>}
          setOrdering={setOrdering}
          ordering={orderings}
          isSkeleton={isSkeleton}
          columnDefinitions={[
            {
              title: intl.formatMessage({
                id: "column.name",
                defaultMessage: "Name",
              }),
              orderingField: "name",
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({ id: "column.id", defaultMessage: "ID" }),
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({
                id: "column.license",
                defaultMessage: "License",
              }),
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({
                id: "column.threedi_organisations",
                defaultMessage: "Rana HCC organisations",
              }),
              width: "table_cell",
            },
            {
              title: intl.formatMessage({
                id: "column.identity_providers",
                defaultMessage: "Identity Providers",
              }),
              width: "table_cell_xs",
            },
          ]}
          tableActions={getTableActions()}
          tableDataIds={tenantList.map((tenant) => tenant.id)}
          hasRowActions={canDeleteTenant}
        >
          <RanaTable.Container minH="fit">
            <RanaTable.Header />
            <RanaTable.Body>
              {tenantList.map((tenant) => {
                const { id, name, license, threediOrganisations, identityProviders } = tenant;
                return (
                  <RanaTable.Row
                    rowActions={canDeleteTenant ? getActions(tenant) : undefined}
                    key={id}
                    rowId={id}
                  >
                    <RanaTable.Cell>
                      <Link as={ReactRouterLink} to={`/${id}/admin/detail`}>
                        {name}
                      </Link>
                    </RanaTable.Cell>
                    <RanaTable.Cell>{id}</RanaTable.Cell>
                    <RanaTable.Cell>{capitalizeFirst(license)}</RanaTable.Cell>
                    <RanaTable.Cell>{listToString(threediOrganisations)}</RanaTable.Cell>
                    <RanaTable.Cell>
                      {listToString(identityProviders.map(({ name }) => name))}
                    </RanaTable.Cell>
                  </RanaTable.Row>
                );
              })}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
        <RanaTable.Pagination
          limit={limit}
          setPageSize={setPageSize}
          page={page}
          numPages={numPages}
          setPage={setPage}
        />
      </Flex>
    </>
  );
}
